'use client';

/**
 * Compact, neutral metric cards with positive/negative/neutral delta
 * styling and a brand-tinted top accent bar.
 * Ported from ThemeMaker's {@code blocks/stats/MetricRow}.
 *
 * <p>Each card shows a label, a 0-padded index in the top-right, a
 * large numeric value, and an optional delta pill with arrow + caption.
 * Reduced-motion users get no transitions or hover lift.
 *
 * <p>Honors the {@code requireAuth} "Visible to" gate: {@code 'any'}
 * always renders; {@code 'in'} only for signed-in users; {@code 'out'}
 * only for anonymous visitors.
 */
import { useCurrentUser } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { tx, asList } from '../_shared/coerce';
import { SectionShell } from '../_shared/SectionShell';
import type { PaddingBox } from '../_shared/resolvePadding';

type Localized = string | Record<string, string>;

interface Metric {
  label?: Localized;
  value?: string;
  delta?: string;
  deltaPositive?: boolean;
  deltaCaption?: Localized;
}

export interface MetricRowProps {
  title?: Localized;
  subtitle?: Localized;
  metrics?: Metric[];
  /**
   * Auth-gate the whole block. {@code 'any'} (default) always renders;
   * {@code 'in'} only for signed-in users; {@code 'out'} only for
   * anonymous visitors.
   */
  requireAuth?: 'any' | 'in' | 'out';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

export function MetricRow({
  title,
  subtitle,
  metrics,
  requireAuth,
  paddingOuter,
  paddingInner,
}: MetricRowProps) {
  const currentUser = useCurrentUser();

  // Auth gate — mirror the shell's requireAuth semantics.
  const isSignedIn = currentUser != null;
  const gate = requireAuth ?? 'any';
  const hidden = (gate === 'in' && !isSignedIn) || (gate === 'out' && isSignedIn);
  if (hidden) return null;

  const list = asList<Metric>(metrics);
  return (
    <SectionShell
      title={tx(title)}
      subtitle={tx(subtitle)}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      <div className="metric-row-grid">
        {list.map((m, i) => (
          <MetricCard key={i} metric={m} index={i} />
        ))}
      </div>
      <style dangerouslySetInnerHTML={{ __html: metricRowCss }} />
    </SectionShell>
  );
}

function MetricCard({ metric, index }: { metric: Metric; index: number }) {
  const hasDelta = Boolean(metric.delta);
  const tone =
    metric.deltaPositive === true
      ? 'positive'
      : metric.deltaPositive === false
        ? 'negative'
        : 'neutral';
  const ariaLabel = tone === 'positive' ? 'Increase' : tone === 'negative' ? 'Decrease' : 'Change';

  return (
    <Card asChild>
      <article className="metric-card">
        <div className="metric-card-top">
          <div className="metric-label">{tx(metric.label)}</div>
          <div className="metric-index" aria-hidden="true">
            {String(index + 1).padStart(2, '0')}
          </div>
        </div>
        <div className="metric-value">{metric.value ?? '—'}</div>
        {hasDelta && (
          <div className="metric-delta-row" aria-label={`${ariaLabel}: ${metric.delta}`}>
            <span className={`metric-delta metric-delta-${tone}`}>
              <span className="metric-delta-icon" aria-hidden="true">
                {tone === 'positive' ? '▲' : tone === 'negative' ? '▼' : '•'}
              </span>
              {metric.delta}
            </span>
            {metric.deltaCaption && (
              <span className="metric-delta-caption">{tx(metric.deltaCaption)}</span>
            )}
          </div>
        )}
      </article>
    </Card>
  );
}

const metricRowCss = `
.metric-row-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: clamp(12px, 2vw, 18px);
}
/* Rendered as <Card>: border + elevation come from the component
   (ring-(--color-border-brand) + shadow-(--shadow-brand)). */
.metric-card {
  position: relative; isolation: isolate;
  min-height: 138px;
  padding: clamp(18px, 2.2vw, 22px);
  border-radius: var(--radius-lg, 16px);
  background:
    linear-gradient(180deg,
      color-mix(in srgb, var(--color-surface-card, #ffffff) 97%, white 3%),
      var(--color-surface-card, #ffffff)
    );
  display: flex; flex-direction: column; gap: 10px;
  overflow: hidden;
  transition: transform 180ms ease, background 180ms ease;
}
.metric-card::before {
  content: ""; position: absolute; inset: 0 0 auto 0; height: 3px;
  background: linear-gradient(to right,
    var(--color-brand-500, #3b82f6), transparent 72%);
  opacity: 0.5; pointer-events: none;
}
.metric-card:hover {
  transform: translateY(-3px);
}
.metric-card-top {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
}
.metric-label {
  min-width: 0;
  color: var(--color-text-secondary, #71717a);
  font-size: 13px; font-weight: 650; line-height: 1.3;
  letter-spacing: 0.01em;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.metric-index {
  flex: 0 0 auto;
  color: var(--color-text-tertiary, #a1a1aa);
  font-family: var(--font-family-body, inherit);
  font-size: 11px; font-weight: 700;
}
.metric-value {
  color: var(--color-text-primary, #0f172a);
  font-family: var(--font-family-display, Inter, system-ui, sans-serif);
  font-size: clamp(30px, 3.2vw, 42px);
  font-weight: 820; line-height: 1;
  letter-spacing: -0.045em;
  font-variant-numeric: tabular-nums;
}
.metric-delta-row {
  margin-top: auto;
  display: flex; align-items: center; gap: 7px;
  min-width: 0;
  font-size: 12px; line-height: 1.3;
}
.metric-delta {
  display: inline-flex; align-items: center; gap: 4px;
  flex: 0 0 auto;
  padding: 4px 7px;
  border-radius: 999px;
  font-weight: 750;
  font-variant-numeric: tabular-nums;
}
.metric-delta-icon { font-size: 9px; line-height: 1; }
.metric-delta-positive {
  color: var(--color-feedback-success, #15803d);
  background: color-mix(in srgb, var(--color-feedback-success, #22c55e) 12%, transparent);
}
.metric-delta-negative {
  color: var(--color-feedback-danger, #b91c1c);
  background: color-mix(in srgb, var(--color-feedback-danger, #ef4444) 10%, transparent);
}
.metric-delta-neutral {
  color: var(--color-text-secondary, #71717a);
  background: var(--color-surface-muted, rgba(120, 120, 140, 0.1));
}
.metric-delta-caption {
  min-width: 0;
  color: var(--color-text-tertiary, #6b7280);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
:where([data-theme="dark"], .dark) .metric-card {
  background:
    linear-gradient(180deg,
      color-mix(in srgb, var(--color-surface-card, #18181b) 94%, white 6%),
      var(--color-surface-card, #18181b));
}
@media (max-width: 520px) {
  .metric-row-grid { grid-template-columns: 1fr; }
  .metric-card { min-height: 124px; padding: 18px; }
  .metric-value { font-size: clamp(30px, 10vw, 38px); }
}
@media (prefers-reduced-motion: reduce) {
  .metric-card { transition: none !important; }
  .metric-card:hover { transform: none !important; }
}
`;
